import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fileUrl, formatDate } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, Upload, FileImage, IdCard, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const empty = { first_name: "", last_name: "", nationality: "", id_type: "passport", id_number: "", phone: "", email: "", address: "", notes: "" };

export default function Tenants() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: tenants = [] } = useQuery({ queryKey: ["tenants"], queryFn: () => api.get("/tenants").then((r) => r.data) });
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docNo, setDocNo] = useState("");
  const fileInput = useRef(null);

  const filtered = tenants.filter((tn) => `${tn.first_name} ${tn.last_name} ${tn.id_number} ${tn.phone}`.toLowerCase().includes(search.toLowerCase()));

  const save = async () => {
    if (!form.first_name || !form.last_name) { toast.error(t("tenants.firstName")); return; }
    const payload = { ...empty, ...form };
    if (form.id) await api.put(`/tenants/${form.id}`, payload);
    else await api.post("/tenants", payload);
    qc.invalidateQueries({ queryKey: ["tenants"] });
    setForm(null);
    toast.success(t("common.save"));
  };

  const remove = async (id) => {
    if (!window.confirm(t("tenants.deleteConfirm"))) return;
    await api.delete(`/tenants/${id}`);
    qc.invalidateQueries({ queryKey: ["tenants"] });
    setDetail(null);
    toast.success(t("common.delete"));
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !detail) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("id_number", docNo || detail.id_number || "");
    fd.append("id_type", detail.id_type || "");
    try {
      const { data } = await api.post(`/tenants/${detail.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = { ...detail, doc_files: [...(detail.doc_files || []), data] };
      setDetail(updated);
      qc.invalidateQueries({ queryKey: ["tenants"] });
      toast.success(t("tenants.upload"));
    } catch { toast.error("Upload error"); }
    setUploading(false);
    e.target.value = "";
  };

  const deleteDoc = async (fid) => {
    await api.delete(`/tenants/${detail.id}/documents/${fid}`);
    setDetail({ ...detail, doc_files: detail.doc_files.filter((d) => d.file_id !== fid) });
    qc.invalidateQueries({ queryKey: ["tenants"] });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak">{t("tenants.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("tenants.subtitle")}</p>
        </div>
        <Button onClick={() => setForm({ ...empty })} className="bg-teak hover:bg-teak-hover text-white" data-testid="add-tenant-btn">
          <Plus className="w-4 h-4 mr-1" /> {t("tenants.new")}
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} className="pl-9" data-testid="tenant-search" />
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-dashed">{t("tenants.empty")}</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((tn) => (
            <Card key={tn.id} className="p-5 border-border hover:shadow-[0_8px_28px_rgba(42,42,42,0.06)] transition-shadow duration-200" data-testid={`tenant-card-${tn.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-teak/10 flex items-center justify-center text-teak font-semibold">
                    {tn.first_name?.[0]}{tn.last_name?.[0]}
                  </div>
                  <div>
                    <div className="font-medium">{tn.first_name} {tn.last_name}</div>
                    <div className="text-xs text-muted-foreground">{tn.nationality || "—"}</div>
                  </div>
                </div>
                {tn.doc_files?.length > 0 && <Badge variant="secondary" className="gap-1"><FileImage className="w-3 h-3" />{tn.doc_files.length}</Badge>}
              </div>
              <div className="mt-4 space-y-1 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><IdCard className="w-3.5 h-3.5" /> {t(`tenants.${tn.id_type === "passport" ? "passport" : "idCard"}`)}: {tn.id_number || "—"}</div>
                <div>{tn.phone || "—"}</div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => { setDetail(tn); setDocNo(tn.id_number || ""); }} className="flex-1" data-testid={`tenant-view-${tn.id}`}>{t("common.view")}</Button>
                <Button variant="ghost" size="icon" onClick={() => setForm({ ...tn })} data-testid={`tenant-edit-${tn.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(tn.id)} data-testid={`tenant-delete-${tn.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? t("tenants.edit") : t("tenants.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t("tenants.firstName")}</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="tenant-firstname" /></div>
              <div className="space-y-2"><Label>{t("tenants.lastName")}</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="tenant-lastname" /></div>
              <div className="space-y-2"><Label>{t("tenants.nationality")}</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} data-testid="tenant-nationality" /></div>
              <div className="space-y-2"><Label>{t("tenants.idType")}</Label>
                <Select value={form.id_type} onValueChange={(v) => setForm({ ...form, id_type: v })}>
                  <SelectTrigger data-testid="tenant-idtype"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="passport">{t("tenants.passport")}</SelectItem><SelectItem value="id_card">{t("tenants.idCard")}</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2"><Label>{t("tenants.idNumber")}</Label><Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} data-testid="tenant-idnumber" /></div>
              <div className="space-y-2"><Label>{t("tenants.phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tenant-phone" /></div>
              <div className="space-y-2"><Label>{t("tenants.email")}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tenant-email" /></div>
              <div className="space-y-2 col-span-2"><Label>{t("tenants.address")}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="tenant-address" /></div>
              <div className="space-y-2 col-span-2"><Label>{t("tenants.notes")}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="tenant-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={save} className="bg-teak hover:bg-teak-hover text-white" data-testid="tenant-save-btn">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog with documents */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.first_name} {detail?.last_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">{t("tenants.nationality")}: </span>{detail.nationality || "—"}</div>
                <div><span className="text-muted-foreground">{t(`tenants.${detail.id_type === "passport" ? "passport" : "idCard"}`)}: </span>{detail.id_number || "—"}</div>
                <div><span className="text-muted-foreground">{t("tenants.phone")}: </span>{detail.phone || "—"}</div>
                <div><span className="text-muted-foreground">{t("tenants.email")}: </span>{detail.email || "—"}</div>
                <div className="col-span-2"><span className="text-muted-foreground">{t("tenants.address")}: </span>{detail.address || "—"}</div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">{t("tenants.documents")}</Label>
                  <input ref={fileInput} type="file" accept="image/*,.pdf" capture="environment" hidden onChange={upload} data-testid="tenant-file-input" />
                  <Button size="sm" variant="outline" onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="tenant-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} {t("tenants.upload")}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{t("tenants.scanHint")}</p>
                <div className="mb-3 max-w-xs">
                  <Label className="text-xs">{t("tenants.idNumber")} ({t(`tenants.${detail.id_type === "passport" ? "passport" : "idCard"}`)})</Label>
                  <Input value={docNo} onChange={(e) => setDocNo(e.target.value)} placeholder={detail.id_number || ""} data-testid="doc-id-number" className="h-9 mt-1" />
                </div>
                {(!detail.doc_files || detail.doc_files.length === 0) ? (
                  <div className="border border-dashed border-border rounded-md p-8 text-center text-muted-foreground text-sm">
                    <FileImage className="w-8 h-8 mx-auto mb-2 opacity-40" /> {t("common.none")}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {detail.doc_files.map((d) => (
                      <div key={d.file_id} className="relative group rounded-md overflow-hidden border border-border" data-testid={`tenant-doc-${d.file_id}`}>
                        {d.content_type?.startsWith("image/") ? (
                          <img src={fileUrl(d.storage_path)} alt={d.original_filename} className="w-full h-32 object-cover" />
                        ) : (
                          <a href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center h-32 bg-secondary text-sm text-teak">
                            <FileImage className="w-8 h-8 mb-1" /> PDF
                          </a>
                        )}
                        <button onClick={() => deleteDoc(d.file_id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`doc-delete-${d.file_id}`}>
                          <X className="w-3.5 h-3.5 text-destructive" />
                        </button>
                        <div className="text-[10px] px-1.5 py-1 bg-white leading-tight">
                          <div className="font-medium text-teak truncate">{d.id_number || "—"}</div>
                          <div className="text-muted-foreground truncate">{d.id_type ? t(`tenants.${d.id_type === "passport" ? "passport" : "idCard"}`) + " · " : ""}{formatDate(d.uploaded_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => remove(detail.id)} className="text-destructive mr-auto"><Trash2 className="w-4 h-4 mr-1" />{t("common.delete")}</Button>
            <Button variant="outline" onClick={() => { setForm({ ...detail }); setDetail(null); }}>{t("common.edit")}</Button>
            <Button onClick={() => setDetail(null)} className="bg-teak hover:bg-teak-hover text-white">{t("common.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
